import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { HttpStatusCode, isAxiosError } from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useStoreSelector } from 'v3/data/store';
import { showErrorDialog } from 'v3/data/store/error/actions';
import {
  clearOnlineContractingInfo,
  setContractingKey,
  setContractingOnlineState,
} from 'v3/data/store/onlineContracting/actions';
import { CreditSimulationOriginEnum, OnlineContractingStateEnum } from 'v3/data/store/onlineContracting/enums';
import { getOriginFromSlug, getOriginSlug, OnlineContractingStateMapping } from 'v3/data/store/onlineContracting/mapping';

import { useBreakpoints } from 'v3/hooks/useBreakpoints';

import { OnlineFinancingInformation } from 'v3/pages/OnlineContracting/Details/Cards/FinancingInformation';
import { OnlineHistoryInformation } from 'v3/pages/OnlineContracting/Details/Cards/HistoryInformation';
import { PersonalInformationInApp } from 'v3/pages/OnlineContracting/Details/Cards/PersonalInformation';
import { OnlineTransactionInformation } from 'v3/pages/OnlineContracting/Details/Cards/TransactionInformation';

import { GO_BACK, RoutesEnum } from 'v3/routes/enum';
import * as onlineApi from 'v3/services/online/api';

import { CircularButton } from 'v3/shared/components/Button/CircularButton';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import { getFinancingStatusDetails } from 'v3/shared/components/Table/OnlineContractingTable/statusHelpers';
import { OnlineCommonTemplate } from 'v3/shared/templates/OnlineCommomTemplate';
import { HeadlineMedium } from 'v3/shared/typography/HeadlineMedium';
import { SubtitleMedium } from 'v3/shared/typography/SubtitleMedium';

import {
  setCancelationModalOpen,
  setClearCancellationState,
  setCurrentCancellationState,
} from 'v3/data/store/cancellationNavigation/actions';
import { CancellationAllowedEnum, CancellationNavigationEnum } from 'v3/data/store/cancellationNavigation/enum';
import {
  CancellationStateMapping,
  CancellationStateMappingDisableToCancel,
} from 'v3/data/store/cancellationNavigation/mapping';
import { PERMISSIONS } from 'v3/data/store/stores/constants';
import { StoreProfileEnum } from 'v3/data/store/stores/enums';
import { CancellationFlowModal } from 'v3/pages/OnlineContracting/Cancellation';
import { useFetchContractingDetails } from 'v3/pages/OnlineContracting/Details/get-contracting-details-hook';
import { theme } from 'v3/styles/global-styles';
import {
  AlignMobileButton,
  AlignStatusAndProof,
  ButtonsWrapper,
  Container,
  Content,
  Header,
  MainTitle,
  RefreshIconStyled,
  StatusBadgeContainer,
  StatusDot,
} from './styles';

type OnlineContractingDetailsParams = {
  contractingKey: string;
  origin?: string;
};

export const OnlineContractingDetailsPage = () => {
  const navigate = useNavigate();
  const { contractingKey, origin: originSlug } = useParams<
    Readonly<OnlineContractingDetailsParams>
  >() as OnlineContractingDetailsParams;
  const origin = getOriginFromSlug(originSlug);
  const { contractingState, paymentDetails } = useStoreSelector((store) => store.onlineContracting);
  const { currentCancelationState } = useStoreSelector((store) => store.cancellationNavigation);
  const { isDesktop } = useBreakpoints();
  const [refundButtonActive, setRefundButtonActive] = useState(false);
  const [cancelButtonActive, setCancelButtonActive] = useState(false);
  const group = useStoreSelector((store) => store.store.storeLogged.group);
  const { fetchContractingDetails } = useFetchContractingDetails();

  const permissions = useMemo(() => {
    return PERMISSIONS[group ?? StoreProfileEnum.EMPTY];
  }, [group]);

  const { isMaster } = permissions;

  const cancellationStateTitle = CancellationStateMapping[currentCancelationState ?? '']?.title;
  const contractingStateTitle = OnlineContractingStateMapping[contractingState]?.title;
  const statusTitle = cancellationStateTitle ?? contractingStateTitle;

  const statusColor = useMemo(() => {
    return getFinancingStatusDetails(contractingState).color;
  }, [contractingState]);

  const isInApp = paymentDetails?.origin === CreditSimulationOriginEnum.InApp;

  const fetchCancellationStatus = useCallback(async () => {
    if (paymentDetails?.origin !== CreditSimulationOriginEnum.InApp) return;

    try {
      const response = await onlineApi.getCancellationStatus(contractingKey);
      setCurrentCancellationState(response.data.status);
    } catch (error) {
      setClearCancellationState();
      if (isAxiosError(error)) {
        if (error.response?.status !== HttpStatusCode.NotFound) {
          showErrorDialog('Erro ao obter dados de cancelamento');
        }
      } else {
        showErrorDialog('Erro ao obter dados de cancelamento');
      }
    }
  }, [contractingKey, paymentDetails?.origin]);

  const handleButtonActivation = useCallback(() => {
    if (!group) return;

    const shouldAllowCancellation = CancellationAllowedEnum.includes(contractingState);
    const hasImei = Boolean(paymentDetails.device?.imei);
    const cancellationAllowed = !CancellationStateMappingDisableToCancel.find(
      (status: CancellationNavigationEnum) => status === currentCancelationState,
    );

    if (!shouldAllowCancellation || isMaster) {
      setCancelButtonActive(false);
      setRefundButtonActive(false);
      return;
    }

    const isEnabledCancelButton = hasImei && cancellationAllowed;
    const isEnabledRefundButton = !hasImei && cancellationAllowed;

    setCancelButtonActive(isEnabledCancelButton);
    setRefundButtonActive(isEnabledRefundButton);
  }, [group, contractingState, paymentDetails.device?.imei, isMaster, currentCancelationState]);
  const updateStatus = useCallback(async () => {
    try {
      const response = await onlineApi.getContractingState(contractingKey, origin);
      setContractingOnlineState(response.data.status);
    } catch (error) {
      if (isAxiosError(error)) {
        const { status } = error.response || {};
        if (status === HttpStatusCode.Locked || status === HttpStatusCode.Gone) {
          setContractingOnlineState(OnlineContractingStateEnum.ExpiredKey);
        }
      } else {
        showErrorDialog('Erro ao obter status da contratação', error);
      }
    }
  }, [contractingKey, origin]);

  const refreshStatus = () => {
    fetchCancellationStatus();
    updateStatus();
    fetchContractingDetails(contractingKey, origin);
  };

  const cancelationFlow = () => {
    handleCancelationFlow(CancellationNavigationEnum.CANCELLATION_NOT_STARTED);
  };

  const refundFlow = () => {
    handleCancelationFlow(CancellationNavigationEnum.REFUND_STARTED);
  };

  const handleCancelationFlow = (cancelationState: CancellationNavigationEnum) => {
    if (!currentCancelationState || currentCancelationState === CancellationNavigationEnum.CANCELLATION_REFUSED) {
      setCurrentCancellationState(cancelationState);
    } else {
      setCurrentCancellationState(currentCancelationState);
    }
    setCancelationModalOpen(true);
  };

  useEffect(() => {
    fetchContractingDetails(contractingKey, origin);
  }, [contractingKey, origin, fetchContractingDetails]);

  useEffect(() => {
    if (currentCancelationState) {
      fetchContractingDetails(contractingKey, origin);
    }
  }, [currentCancelationState, contractingKey, origin, fetchContractingDetails]);

  useEffect(() => {
    handleButtonActivation();
  }, [handleButtonActivation]);

  useEffect(() => {
    clearOnlineContractingInfo();
    setContractingKey(contractingKey);
    updateStatus();
  }, [contractingKey, updateStatus]);

  useEffect(() => {
    if (paymentDetails?.origin === CreditSimulationOriginEnum.InApp) {
      fetchCancellationStatus();
    }
  }, [paymentDetails?.origin, fetchCancellationStatus]);

  useEffect(() => {
    if (!originSlug && paymentDetails?.origin) {
      const slug = getOriginSlug(paymentDetails.origin);
      if (slug) {
        navigate(`${RoutesEnum.ContractingOnline}/${slug}/${contractingKey}`, { replace: true });
      }
    }
  }, [originSlug, paymentDetails?.origin, contractingKey, navigate]);

  return (
    <OnlineCommonTemplate>
      <Container>
        <Content>
          <Header>
            <MainTitle>
              <ArrowBackIcon className="pointer" fontSize="large" onClick={() => navigate(GO_BACK)} />
              <HeadlineMedium marginRight="auto">Detalhes da proposta</HeadlineMedium>
            </MainTitle>
            <AlignStatusAndProof>
              {!isDesktop ? 
                <>
                  <AlignMobileButton>
                    {isInApp 
                      && <CircularButton
                        startIcon={ArrowBackIcon}
                        handleClick={refundFlow}
                        disabled={!refundButtonActive}
                      />
                    }
                    {isInApp 
                      && <CircularButton
                        startIcon={HighlightOffIcon}
                        handleClick={cancelationFlow}
                        disabled={!cancelButtonActive}
                      />
                    }
                  </AlignMobileButton>
                  <StatusBadgeContainer>
                    <StatusDot color={statusColor} />
                    <SubtitleMedium color={theme.palette.info.main}>{statusTitle}</SubtitleMedium>
                    <RefreshIconStyled onClick={refreshStatus} />
                  </StatusBadgeContainer>
                </>
                :                 <>
                  <ButtonsWrapper>
                    {isInApp 
                      && <FlexButton
                        size="small"
                        text="Estorno"
                        startIcon={ArrowBackIcon}
                        handleClick={refundFlow}
                        disabled={!refundButtonActive}
                      />
                    }
                    {isInApp 
                      && <FlexButton
                        size="small"
                        text="Cancelar"
                        startIcon={HighlightOffIcon}
                        handleClick={cancelationFlow}
                        disabled={!cancelButtonActive}
                      />
                    }
                  </ButtonsWrapper>
                  <StatusBadgeContainer>
                    <StatusDot color={statusColor} />
                    <SubtitleMedium color={theme.palette.info.main}>{statusTitle}</SubtitleMedium>
                    <RefreshIconStyled onClick={refreshStatus} />
                  </StatusBadgeContainer>
                </>
              }
            </AlignStatusAndProof>
          </Header>
          <OnlineTransactionInformation />
          <PersonalInformationInApp />
          <OnlineFinancingInformation />
          <OnlineHistoryInformation />
        </Content>
        <CancellationFlowModal />
      </Container>
    </OnlineCommonTemplate>
  );
};
